import React, { useEffect, useState } from 'react';
import { Database, Trash2, Clock, RefreshCw, AlertCircle } from 'lucide-react';
import { zohoCrmService } from '../services/zohoCrmService';
import { useWorkspace } from '../contexts/WorkspaceContext';
export function SendBucketManager() {
  const {
    currentUser
  } = useWorkspace();
  const [activeBuckets, setActiveBuckets] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [clearingBucket, setClearingBucket] = useState<string | null>(null);
  const [clearResult, setClearResult] = useState<any>(null);
  const loadBuckets = async () => {
    setIsLoading(true);
    try {
      const buckets = await zohoCrmService.getActiveBuckets();
      setActiveBuckets(buckets);
    } catch (error) {
      console.error('Failed to load buckets:', error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    loadBuckets();
  }, []);
  const handleClearBucket = async (bucketId: string) => {
    if (!confirm(`Clear Send Bucket ${bucketId}? This will remove the bucket from all contacts.`)) {
      return;
    }
    setClearingBucket(bucketId);
    try {
      const result = await zohoCrmService.clearSendBucket(bucketId);
      setClearResult({
        bucketId,
        ...result
      });
      await loadBuckets(); // Reload list
    } catch (error) {
      setClearResult({
        bucketId,
        success: false,
        errors: [error instanceof Error ? error.message : 'Failed to clear bucket']
      });
    } finally {
      setClearingBucket(null);
      setTimeout(() => setClearResult(null), 5000);
    }
  };
  const handleClearExpired = async () => {
    if (!confirm('Clear all expired Send Buckets?')) {
      return;
    }
    setIsLoading(true);
    try {
      const result = await zohoCrmService.clearExpiredBuckets();
      setClearResult({
        bucketId: 'expired',
        ...result
      });
      await loadBuckets();
    } catch (error) {
      setClearResult({
        bucketId: 'expired',
        success: false,
        errors: [error instanceof Error ? error.message : 'Failed to clear expired buckets']
      });
    } finally {
      setIsLoading(false);
      setTimeout(() => setClearResult(null), 5000);
    }
  };
  const isExpired = (expiresAt?: string) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) <= new Date();
  };
  const formatExpiry = (expiresAt?: string) => {
    if (!expiresAt) return 'No expiry';
    const date = new Date(expiresAt);
    const now = new Date();
    if (date <= now) return 'Expired';
    const hoursLeft = Math.round((date.getTime() - now.getTime()) / (1000 * 60 * 60));
    if (hoursLeft < 24) return `${hoursLeft}h remaining`;
    const daysLeft = Math.round(hoursLeft / 24);
    return `${daysLeft}d remaining`;
  };
  // Security check
  if (currentUser.role !== 'Super Admin' && currentUser.role !== 'Workspace Admin') {
    return <div className="p-8 text-center" data-id="element-1639">
        <AlertCircle className="mx-auto h-12 w-12 text-gray-400" data-id="element-1640" />
        <h2 className="mt-4 text-lg font-medium text-gray-900" data-id="element-1641">
          Access Denied
        </h2>
        <p className="mt-2 text-sm text-gray-500" data-id="element-1642">
          Only Admins can manage Send Buckets.
        </p>
      </div>;
  }
  return <div className="space-y-6" data-id="element-1643">
      <div className="flex items-center justify-between" data-id="element-1644">
        <div data-id="element-1645">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-1646">
            Send Bucket Manager
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-1647">
            Manage active Send Buckets in Zoho CRM.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-1648">
          <button onClick={loadBuckets} disabled={isLoading} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50" data-id="element-1649">
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} data-id="element-1650" />
            Refresh
          </button>
          <button onClick={handleClearExpired} disabled={isLoading} className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 disabled:opacity-50" data-id="element-1651">
            <Clock className="h-4 w-4" data-id="element-1652" />
            Clear Expired
          </button>
        </div>
      </div>

      {/* Mode Indicator */}
      {zohoCrmService.isMockMode() && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1653">
          <p className="text-sm text-blue-700" data-id="element-1654">
            <strong data-id="element-1655">Demo Mode:</strong> Currently running in mock mode. Changes
            will not affect real CRM data.
          </p>
        </div>}

      {/* Clear Result */}
      {clearResult && <div className={`rounded-lg p-4 ${clearResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-id="element-1656">
          <p className={`text-sm font-medium ${clearResult.success ? 'text-green-900' : 'text-red-900'}`} data-id="element-1657">
            {clearResult.success ? `Successfully cleared ${clearResult.updatedCount} contact(s) from bucket ${clearResult.bucketId}` : `Failed to clear bucket ${clearResult.bucketId}`}
          </p>
          {clearResult.errors?.length > 0 && <ul className="mt-1 text-xs text-red-700 space-y-1" data-id="element-1658">
              {clearResult.errors.map((error: string, i: number) => <li key={i} data-id="element-1659">• {error}</li>)}
            </ul>}
        </div>}

      {/* Buckets List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-1660">
        {isLoading ? <div className="p-12 text-center" data-id="element-1661">
            <RefreshCw className="mx-auto h-8 w-8 text-gray-400 animate-spin" data-id="element-1662" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-1663">Loading buckets...</p>
          </div> : activeBuckets.length === 0 ? <div className="p-12 text-center" data-id="element-1664">
            <Database className="mx-auto h-12 w-12 text-gray-400" data-id="element-1665" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-1666">
              No Active Buckets
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-1667">
              No Send Buckets are currently active in the CRM.
            </p>
          </div> : <table className="min-w-full divide-y divide-gray-200" data-id="element-1668">
            <thead className="bg-gray-50" data-id="element-1669">
              <tr data-id="element-1670">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1671">
                  Bucket ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1672">
                  Contacts
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1673">
                  Expiry
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1674">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1675">
              {activeBuckets.map(bucket => <tr key={bucket.bucketId} className="hover:bg-gray-50" data-id="element-1676">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1677">
                    <code className="text-sm font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded" data-id="element-1678">
                      {bucket.bucketId}
                    </code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1679">
                    {bucket.contactCount.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1680">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${isExpired(bucket.expiresAt) ? 'bg-red-100 text-red-800' : bucket.expiresAt ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1681">
                      <Clock className="h-3 w-3" data-id="element-1682" />
                      {formatExpiry(bucket.expiresAt)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-1683">
                    <button onClick={() => handleClearBucket(bucket.bucketId)} disabled={clearingBucket === bucket.bucketId} className="inline-flex items-center gap-1 text-red-600 hover:text-red-900 disabled:opacity-50" title="Clear Bucket" data-id="element-1684">
                      {clearingBucket === bucket.bucketId ? <RefreshCw className="h-4 w-4 animate-spin" data-id="element-1685" /> : <Trash2 className="h-4 w-4" data-id="element-1686" />}
                      <span className="text-sm" data-id="element-1687">Clear</span>
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>}
      </div>

      {/* Instructions */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-id="element-1688">
        <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1689">
          How to use Send Buckets
        </h3>
        <ol className="space-y-2 text-sm text-gray-600" data-id="element-1690">
          <li className="flex gap-2" data-id="element-1691">
            <span className="font-medium text-gray-900" data-id="element-1692">1.</span>
            <span data-id="element-1693">Export a campaign to create a Send Bucket in Zoho CRM</span>
          </li>
          <li className="flex gap-2" data-id="element-1694">
            <span className="font-medium text-gray-900" data-id="element-1695">2.</span>
            <span data-id="element-1696">
              In Zoho Campaigns, create a segment with filter:{' '}
              <code className="bg-white px-1 rounded text-xs" data-id="element-1697">
                Send_Bucket_ID equals [bucket-id]
              </code>
            </span>
          </li>
          <li className="flex gap-2" data-id="element-1698">
            <span className="font-medium text-gray-900" data-id="element-1699">3.</span>
            <span data-id="element-1700">
              Send your campaign through Zoho Campaigns using that segment
            </span>
          </li>
          <li className="flex gap-2" data-id="element-1701">
            <span className="font-medium text-gray-900" data-id="element-1702">4.</span>
            <span data-id="element-1703">
              After sending, clear the bucket here to free up contacts for
              future campaigns
            </span>
          </li>
        </ol>
      </div>
    </div>;
}